import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Zap } from "lucide-react";

export default function Legal({ docType }) {
  const params = useParams();
  const type = docType || params.type; // 'terms' or 'privacy'
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/legal/${type}`)
      .then(({ data }) => setDoc(data))
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [type]);

  return (
    <div className="min-h-screen bg-page text-ink">
      <header className="border-b-2 border-ink sticky top-0 bg-page z-20 pt-safe">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-coral border-2 border-ink shadow-brutalSm flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
            <span className="font-display font-black text-lg">Linkola</span>
          </Link>
          <button onClick={() => navigate(-1)} className="text-xs font-bold border-2 border-ink px-3 py-2 inline-flex items-center gap-1 bg-page" data-testid="legal-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid={`legal-${type}`}>
        {loading && <p className="overline">Loading…</p>}
        {!loading && !doc && <p className="text-coral font-bold">Document not found.</p>}
        {!loading && doc && (
          <>
            <article className="prose-legal">
              <MarkdownRender source={doc.content} />
            </article>
            {doc.updated_at && (
              <p className="mt-12 text-[10px] uppercase font-bold tracking-widest text-ink/50 border-t-2 border-ink pt-3">
                Last updated: {doc.updated_at?.slice(0, 10)}
              </p>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// Minimal in-house markdown rendering — supports # ## ### lists, bold (**) and paragraphs.
function MarkdownRender({ source }) {
  const blocks = [];
  const lines = (source || "").split("\n");
  let i = 0;
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      blocks.push(<ul key={`ul-${blocks.length}`} className="list-disc pl-5 space-y-1 my-3 text-sm">{listBuf.map((t, ix) => <li key={ix}><InlineFmt text={t} /></li>)}</ul>);
      listBuf = [];
    }
  };
  while (i < lines.length) {
    const ln = lines[i];
    if (ln.startsWith("# ")) {
      flushList();
      blocks.push(<h1 key={i} className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-6 mb-3 break-words">{ln.slice(2)}</h1>);
    } else if (ln.startsWith("## ")) {
      flushList();
      blocks.push(<h2 key={i} className="font-display font-black text-xl sm:text-2xl tracking-tighter mt-6 mb-2 break-words">{ln.slice(3)}</h2>);
    } else if (ln.startsWith("### ")) {
      flushList();
      blocks.push(<h3 key={i} className="font-display font-black text-lg mt-4 mb-2 break-words">{ln.slice(4)}</h3>);
    } else if (ln.startsWith("- ")) {
      listBuf.push(ln.slice(2));
    } else if (ln.trim() === "") {
      flushList();
    } else {
      flushList();
      blocks.push(<p key={i} className="text-sm sm:text-base leading-relaxed my-2 break-words"><InlineFmt text={ln} /></p>);
    }
    i++;
  }
  flushList();
  return <>{blocks}</>;
}

function InlineFmt({ text }) {
  // Bold via **...**, inline code via `...`
  const parts = [];
  let rest = text;
  let key = 0;
  while (rest.length) {
    const b = rest.indexOf("**");
    const c = rest.indexOf("`");
    const next = [b, c].filter((x) => x >= 0).sort((a, b) => a - b)[0];
    if (next === undefined) { parts.push(rest); break; }
    if (next > 0) { parts.push(rest.slice(0, next)); rest = rest.slice(next); }
    if (rest.startsWith("**")) {
      const end = rest.indexOf("**", 2);
      if (end < 0) { parts.push(rest); break; }
      parts.push(<strong key={key++} className="font-bold">{rest.slice(2, end)}</strong>);
      rest = rest.slice(end + 2);
    } else if (rest.startsWith("`")) {
      const end = rest.indexOf("`", 1);
      if (end < 0) { parts.push(rest); break; }
      parts.push(<code key={key++} className="bg-cream border border-ink/30 px-1 text-xs">{rest.slice(1, end)}</code>);
      rest = rest.slice(end + 1);
    }
  }
  return <>{parts}</>;
}
